import axios from 'axios';

// Core API - use environment variable from Cloudflare Pages
// Development: empty string (relative path)
// Production: https://core.tadagram.com (set in Cloudflare Pages Settings)
const CORE_API_URL = import.meta.env.VITE_CORE_API_URL || '';

export interface TelegramUser {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  language_code?: string;
  is_premium?: boolean;
}

export interface InitMiniAppRequest {
  telegram_id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  init_data: string;
}

export interface InitMiniAppResponse {
  session_token: string;
  user: {
    id: string;
    telegram_id: number;
    first_name: string;
    last_name: string;
    username: string;
    language_code: string;
    is_premium: boolean;
  };
  expires_at: string;
}

export interface TelegramWidgetUser {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
  auth_date: number;
  hash: string;
}

class AuthService {
  private sessionToken: string | null = null;
  private authType: 'miniapp' | 'widget' | null = null;
  private businessId: string | null = null;
  private tokenSubscribers: ((token: string) => void)[] = [];

  /**
   * Check if running in web browser (not Telegram Mini App)
   * Real Mini App will have initData available
   */
  isWebEnvironment(): boolean {
    // Check if Telegram WebApp SDK is loaded
    if (!window.Telegram?.WebApp) {
      return true; // No Telegram SDK = web browser
    }

    // Check if we have actual initData (only available in Mini App)
    const initData = window.Telegram.WebApp.initData;
    return !initData || initData.trim() === '';
  }

  /**
   * Initialize authentication when Mini App loads
   * Calls svc-core-api to verify Telegram initData and get session token
   */
  async initMiniApp(): Promise<boolean> {
    try {
      // Check if running in Telegram Mini App
      if (!window.Telegram?.WebApp) {
        console.error('Not running in Telegram Mini App context');
        return false;
      }

      const tg = window.Telegram.WebApp;
      tg.ready();

      // Get Telegram user data
      const user = tg.initDataUnsafe?.user;
      if (!user) {
        console.error('No Telegram user data available');
        return false;
      }

      // Get initData for backend verification
      const initData = tg.initData;
      if (!initData) {
        console.error('No Telegram initData available');
        return false;
      }

      // Check if we already have a valid session token in CloudStorage
      const storedToken = await this.getStoredSessionToken();
      if (storedToken && await this.verifySessionToken(storedToken)) {
        this.sessionToken = storedToken;
        return true;
      }

      // No valid session, create new one
      const response = await axios.post(
        `${CORE_API_URL}/api/auth/init-mini-app`,
        {
          telegram_id: user.id,
          first_name: user.first_name,
          last_name: user.last_name,
          username: user.username,
          init_data: initData,
        } as InitMiniAppRequest
      );

      if (response.data.session_token) {
        this.sessionToken = response.data.session_token;
        this.authType = 'miniapp';
        // Store in Telegram CloudStorage (persists across sessions)
        await this.storeSessionToken(this.sessionToken);
        return true;
      }

      return false;
    } catch (error) {
      console.error('Failed to initialize Mini App authentication:', error);
      return false;
    }
  }

  /**
   * Initialize authentication via Telegram Login Widget (web browser)
   * Calls svc-core-api to verify widget auth data and get session token
   */
  async initWebLogin(widgetUser: TelegramWidgetUser): Promise<boolean> {
    try {
      // Build auth data string for backend verification
      const authData = {
        id: widgetUser.id,
        first_name: widgetUser.first_name,
        last_name: widgetUser.last_name,
        username: widgetUser.username,
        photo_url: widgetUser.photo_url,
        auth_date: widgetUser.auth_date,
        hash: widgetUser.hash
      };

      // Call backend to verify widget auth and create session
      const response = await axios.post(
        `${CORE_API_URL}/api/auth/init-web-widget`,
        {
          telegram_id: widgetUser.id,
          first_name: widgetUser.first_name,
          last_name: widgetUser.last_name,
          username: widgetUser.username,
          service_name: 'business-chatbot', // Service identifier for bot token lookup
          auth_data: authData,
        }
      );

      if (response.data.session_token) {
        this.sessionToken = response.data.session_token;
        this.authType = 'widget';
        // Store in localStorage for web environment
        this.storeWebSessionToken(this.sessionToken);
        return true;
      }

      return false;
    } catch (error) {
      console.error('Failed to initialize web widget authentication:', error);
      return false;
    }
  }

  /**
   * Check for existing web session token
   */
  async checkWebSession(): Promise<boolean> {
    try {
      const token = this.getWebSessionToken();
      if (token && await this.verifySessionToken(token)) {
        this.sessionToken = token;
        this.authType = 'widget';
        return true;
      }
      return false;
    } catch (error) {
      console.error('Failed to check web session:', error);
      return false;
    }
  }

  /**
   * Get current session token
   */
  getSessionToken(): string | null {
    return this.sessionToken;
  }

  /**
   * Check if currently authenticated
   */
  isAuthenticated(): boolean {
    return !!this.sessionToken;
  }

  /**
   * Set auth token directly (used when token arrives via postMessage from biztada-app).
   * Also persists businessId if provided.
   */
  setAuthToken(token: string, businessId?: string): void {
    this.sessionToken = token;
    this.authType = 'widget';
    localStorage.setItem('session_token', token);
    if (businessId) {
      this.businessId = businessId;
      localStorage.setItem('oauth_business_id', businessId);
    }
    console.log('[Auth] JWT set from shell postMessage');
    
    // Notify any pending API requests waiting for a token refresh
    const subs = [...this.tokenSubscribers];
    this.tokenSubscribers = [];
    subs.forEach((cb) => cb(token));
  }

  /**
   * Wait for a new token to be set via setAuthToken (e.g. from shell refresh).
   * Resolves with the new token or null if it times out.
   */
  waitForNewToken(timeoutMs = 10000): Promise<string | null> {
    return new Promise((resolve) => {
      let timeoutId: any;
      const callback = (token: string) => {
        clearTimeout(timeoutId);
        resolve(token);
      };
      timeoutId = setTimeout(() => {
        this.tokenSubscribers = this.tokenSubscribers.filter((cb) => cb !== callback);
        resolve(null);
      }, timeoutMs);
      this.tokenSubscribers.push(callback);
    });
  }

  /**
   * Get current business ID (set from postMessage or OAuth2 exchange).
   * Used to inject X-Business-ID header where needed.
   */
  getBusinessId(): string | null {
    return this.businessId || localStorage.getItem('oauth_business_id');
  }

  /**
   * Load a previously-stored iframe JWT from localStorage for instant resume.
   * Called on startup so the app shows content immediately instead of a blank
   * screen while waiting for the shell's postMessage.
   *
   * Returns true if a cached token was found (not verified — API 401 will signal re-auth).
   */
  loadCachedIframeToken(): boolean {
    const token = localStorage.getItem('session_token');
    const businessId = localStorage.getItem('oauth_business_id') || undefined;
    if (token) {
      this.sessionToken = token;
      this.authType = 'widget';
      if (businessId) this.businessId = businessId;
      return true;
    }
    return false;
  }

  /**
   * Store session token in Telegram CloudStorage
   */
  private async storeSessionToken(token: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!window.Telegram?.WebApp?.CloudStorage) {
        console.warn('CloudStorage not available, using localStorage fallback');
        localStorage.setItem('session_token', token);
        resolve();
        return;
      }

      window.Telegram.WebApp.CloudStorage.setItem('session_token', token, (error, success) => {
        if (error) {
          console.error('Failed to store session token:', error);
          // Fallback to localStorage
          localStorage.setItem('session_token', token);
          reject(error);
        } else {
          resolve();
        }
      });
    });
  }

  /**
   * Store session token in localStorage (for web widget)
   */
  private storeWebSessionToken(token: string): void {
    localStorage.setItem('session_token', token);
  }

  /**
   * Get session token from localStorage (for web widget)
   */
  private getWebSessionToken(): string | null {
    return localStorage.getItem('session_token');
  }

  /**
   * Retrieve session token from Telegram CloudStorage
   */
  private async getStoredSessionToken(): Promise<string | null> {
    return new Promise((resolve) => {
      if (!window.Telegram?.WebApp?.CloudStorage) {
        console.warn('CloudStorage not available, using localStorage fallback');
        resolve(localStorage.getItem('session_token'));
        return;
      }

      window.Telegram.WebApp.CloudStorage.getItem('session_token', (error, value) => {
        if (error || !value) {
          // Fallback to localStorage
          resolve(localStorage.getItem('session_token'));
        } else {
          resolve(value);
        }
      });
    });
  }

  /**
   * Verify if session token is still valid
   */
  private async verifySessionToken(token: string): Promise<boolean> {
    try {
      const response = await axios.get(`${CORE_API_URL}/api/auth/verify-session`, {
        params: { token },
      });
      return response.data.valid === true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Get authentication headers for API requests
   * Returns appropriate headers based on authentication type
   */
  getAuthHeaders(): Record<string, string> {
    const headers: Record<string, string> = {};

    // Priority 1: Session token (if available)
    if (this.sessionToken) {
      headers['Authorization'] = `Bearer ${this.sessionToken}`;
      // Include business context (required by backend)
      const bid = this.businessId || localStorage.getItem('oauth_business_id');
      if (bid) headers['X-Business-ID'] = bid;
      return headers;
    }

    // Priority 2: Telegram initData (Mini App)
    if (window.Telegram?.WebApp?.initData) {
      headers['X-Telegram-Init-Data'] = window.Telegram.WebApp.initData;
      return headers;
    }

    return headers;
  }

  /**
   * Exchange a single-use OAuth2 authorization code for a JWT pair.
   * Called on app load when biztada.com redirects with ?code=...&client_id=...
   *
   * The received access_token is stored as the session_token so the rest of
   * the app (apiService headers, verifySession on refresh) works without change.
   */
  async exchangeOAuthCode(code: string, clientId: string): Promise<boolean> {
    try {
      // Also read business_id from URL (biztada passes it as query param)
      const urlParams = new URLSearchParams(window.location.search);
      const urlBusinessId = urlParams.get('business_id');

      // Use plain axios.post() without generic (matches chatbot tsconfig convention)
      const response = await axios.post(
        `${CORE_API_URL}/api/auth/token`,
        { code, client_id: clientId }
      );
      const data = response.data as {
        success: boolean;
        access_token: string;
        refresh_token: string;
        expires_in: number;
        expires_at: string;
        business_id?: string;
      };

      if (data.success && data.access_token) {
        this.sessionToken = data.access_token;
        this.authType = 'widget';
        // Store business_id from token response (preferred) or URL param
        const bid = data.business_id || urlBusinessId;
        if (bid) {
          this.businessId = bid;
          localStorage.setItem('oauth_business_id', bid);
        }
        // Persist in localStorage so subsequent reloads re-use the JWT
        this.storeWebSessionToken(this.sessionToken);
        console.log('[OAuth] Code exchanged successfully for JWT, business_id:', bid);
        return true;
      }
      console.error('[OAuth] Exchange response missing access_token:', data);
      return false;
    } catch (error) {
      console.error('[OAuth] Code exchange request failed:', error);
      return false;
    }
  }

  /**
   * Logout - clear session token
   */
  async logout(): Promise<void> {
    this.sessionToken = null;
    localStorage.removeItem('session_token');
    
    if (window.Telegram?.WebApp?.CloudStorage) {
      window.Telegram.WebApp.CloudStorage.removeItem('session_token', () => {
        console.log('Session token removed from CloudStorage');
      });
    }
  }
}

export const authService = new AuthService();
